import React, { useState, useEffect } from "react";
import { usePersistedNumber } from "../../hooks/usePersistedNumber";
import {
  Calculator,
  Package,
  Settings,
  FileText,
} from "lucide-react";
import {
  useBatchPlanningMutation,
  CalculateBatchPlanRequest,
  BatchPlanControlMode,
  ProductSizeConstraint,
  ManufactureType,
} from "../../api/hooks/useBatchPlanning";
import { PAGE_CONTAINER_HEIGHT } from "../../constants/layout";
import CatalogAutocomplete from "../common/CatalogAutocomplete";
import { CatalogItemDto, ProductType, CreateManufactureOrderRequest, CreateManufactureOrderProductRequest } from "../../api/generated/api-client";
import { useCreateManufactureOrder } from "../../api/hooks/useManufactureOrders";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../api/client";
import ManufactureOrderDetail from "../manufacture/pages/ManufactureOrderDetail";
import { usePlanningList } from "../../contexts/PlanningListContext";
import { useCatalogAutocomplete } from "../../api/hooks/useCatalogAutocomplete";
import NdStar from "../common/NdStar";
import { TimePeriod, resolveTimePeriod, getTimePeriodDisplayText } from '../../utils/timePeriod';
import { calculateTimePeriodRange } from '../../api/hooks/useManufacturingStockAnalysis';
import { useScreenView } from '../../telemetry/useScreenView';

const SALES_MULTIPLIER_STORAGE_KEY = "batchPlanning.salesMultiplier";
const ND_COVERAGE_FACTOR_STORAGE_KEY = "batchPlanning.ndCoverageFactor";

const BatchPlanningCalculator: React.FC = () => {
  useScreenView('Manufacturing', 'ManufactureBatchPlanning');
  // Selected semiproduct state
  const [selectedSemiproduct, setSelectedSemiproduct] = useState<CatalogItemDto | null>(null);
  
  // State for triggering autocomplete search when pre-filling from planning list
  const [triggerSearch, setTriggerSearch] = useState<string>("");
  
  // Flag to prevent infinite loops when auto-triggering from URL
  const [hasAutoTriggered, setHasAutoTriggered] = useState<boolean>(false);
  
  // State for manufacturing dates when pre-filled from planning list
  const [prefilledManufacturingDate, setPrefilledManufacturingDate] = useState<Date | null>(null);
  
  // Form state
  const [mmqMultiplier, setMmqMultiplier] = useState<number>(1.0);
  const [totalBatchSize, setTotalBatchSize] = useState<number>(0);
  const [targetDaysCoverage, setTargetDaysCoverage] = useState<number>(30);
  const [, setInputMode] = useState<'multiplier' | 'total'>('multiplier'); // Which field user is editing
  
  // Control mode selection
  const [controlMode, setControlMode] = useState<BatchPlanControlMode>(BatchPlanControlMode.MmqMultiplier);
  
  // Sales settings state
  const [salesMultiplier, setSalesMultiplier] = usePersistedNumber(SALES_MULTIPLIER_STORAGE_KEY, 1.3, { min: 0.1, max: 9.9 });
  // Weight of ND (star) sizes' target coverage: 1.2 = ND sizes end with 20 % more days of stock.
  const [ndCoverageFactor, setNdCoverageFactor] = usePersistedNumber(ND_COVERAGE_FACTOR_STORAGE_KEY, 1.0, { min: 0.1, max: 5 });
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>(TimePeriod.Q9M);
  // Only used when selectedTimePeriod === TimePeriod.CustomPeriod.
  // Seeded from Q9M's primary range so the Custom pickers open with a sensible default.
  const [fromDate, setFromDate] = useState<Date>(
    resolveTimePeriod(TimePeriod.Q9M).primary?.from ?? new Date()
  );
  const [toDate, setToDate] = useState<Date>(
    resolveTimePeriod(TimePeriod.Q9M).primary?.to ?? new Date()
  );
  
  // Product constraints state (for fixed quantities)
  const [productConstraints, setProductConstraints] = useState<Map<string, { isFixed: boolean; quantity: number }>>(new Map());
  
  // Modal state for showing manufacture order detail
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [createdOrderDate, setCreatedOrderDate] = useState<Date | null>(null);
  
  // Direct semiproduct amount (MultiPhase only): kg reserved as bulk sellable output
  const [directSemiproductAmount, setDirectSemiproductAmount] = useState<number>(0);

  // State to track if recalculation is needed
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  
  // Mutation
  const batchPlanMutation = useBatchPlanningMutation();
  const createOrderMutation = useCreateManufactureOrder();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Planning list functionality
  const { removeItem } = usePlanningList();
  
  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Trigger autocomplete search when pre-filling from planning list
  useCatalogAutocomplete(
    triggerSearch.length >= 2 ? triggerSearch : undefined,
    50,
    [ProductType.SemiProduct,ProductType.Product],
    true
  );

  // Get API response data
  const response = batchPlanMutation.data;
  
  // Get real MMQ from API response or fallback to 0 if no response yet
  const productMMQ = response?.success && response.semiproduct 
    ? response.semiproduct.minimalManufactureQuantity 
    : 0;
  
  // Get API product sizes
  const apiProductSizes = response?.productSizes || [];

  // Handle prefilled data from URL parameters (from ManufactureBatchCalculator or planning list)
  useEffect(() => {
    const productCode = searchParams.get('productCode');
    const productName = searchParams.get('productName');
    const batchSize = searchParams.get('batchSize');
    const dateParam = searchParams.get('date');
    
    if (productCode && productName) {
      console.log('Processing prefilled data from URL:', { 
        productCode, 
        productName,
        batchSize,
        dateParam
      });
      
      // Trigger autocomplete search to load data for the combobox
      // This will pre-load matching products based on the shortened product code
      setTriggerSearch(productName);
      
      // Create CatalogItemDto from URL parameters
      const prefilledProduct = new CatalogItemDto({
        productCode,
        productName,
        type: ProductType.SemiProduct
      });
      
      // Set the prefilled product
      setSelectedSemiproduct(prefilledProduct);

      // Handle date pre-filling if coming from planning list
      if (dateParam) {
        try {
          const planningDate = new Date(dateParam);
          console.log('Pre-filling manufacturing date from planning list:', planningDate);
          // Store the selected date for manufacturing
          setPrefilledManufacturingDate(planningDate);
        } catch (error) {
          console.error('Error parsing date from planning list:', error);
        }
      }
      
      // Handle batch size if provided (from ManufactureBatchCalculator)
      if (batchSize) {
        // Set control mode to total weight and set the prefilled batch size
        setControlMode(BatchPlanControlMode.TotalWeight);
        setTotalBatchSize(parseFloat(batchSize));
        
        // Trigger the API call directly
        const requestData: any = {
          productCode: productCode,
          controlMode: BatchPlanControlMode.TotalWeight,
          fromDate: fromDate,
          toDate: toDate,
          salesMultiplier: salesMultiplier,
          ndCoverageFactor: ndCoverageFactor,
          totalWeightToUse: parseFloat(batchSize),
        };

        console.log('Triggering batch plan calculation with:', requestData);
        const request = new CalculateBatchPlanRequest(requestData);
        batchPlanMutation.mutate(request);
      }

      // Remove item from planning list when planning process starts
      removeItem(productCode);
      
      // Clear URL parameters to clean up the URL
      setSearchParams({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid infinite loops

  // Separate useEffect to trigger batch planning calculation when semiproduct is selected from URL
  useEffect(() => {
    const productCodeParam = searchParams.get('productCode');
    const productNameParam = searchParams.get('productName');
    const batchSizeParam = searchParams.get('batchSize');
    
    if (selectedSemiproduct && productCodeParam && productNameParam && !hasAutoTriggered && !batchSizeParam) {
      // This means we just pre-filled from URL parameters and haven't triggered yet
      console.log('Triggering batch plan calculation for pre-filled product:', selectedSemiproduct);
      
      // Reset form values (same as handleSemiproductSelect does)
      setMmqMultiplier(1.0);
      setTotalBatchSize(0);
      setTargetDaysCoverage(30);
      
      // Trigger batch planning calculation with default values
      const requestData: any = {
        productCode: selectedSemiproduct.productCode,
        controlMode: BatchPlanControlMode.MmqMultiplier,
        fromDate: fromDate,
        toDate: toDate,
        salesMultiplier: salesMultiplier,
          ndCoverageFactor: ndCoverageFactor,
        mmqMultiplier: 1.0,
      };

      console.log('Auto-triggering batch plan calculation with:', requestData);
      const request = new CalculateBatchPlanRequest(requestData);
      batchPlanMutation.mutate(request);
      
      // Set flag to prevent re-triggering
      setHasAutoTriggered(true);
    }
  }, [selectedSemiproduct, hasAutoTriggered, fromDate, toDate, salesMultiplier, ndCoverageFactor, batchPlanMutation, searchParams]);

  // Update local state when API response changes
  useEffect(() => {
    if (response?.summary) {
      // Update values from API response
      if (response.summary.effectiveMmqMultiplier != null) {
        setMmqMultiplier(response.summary.effectiveMmqMultiplier);
      }
      if (response.summary.actualTotalWeight != null) {
        setTotalBatchSize(response.summary.actualTotalWeight);
      }
    }
  }, [response]);
  
  // Convert API data to grid format
  const productGridData = apiProductSizes.map((product) => ({
    productCode: product.productCode || '',
    productName: product.productName || '',
    netWeight: product.weightPerUnit || 0,
    stockEshopTotal: product.currentStock || 0,
    plannedQuantity: product.plannedQuantity || 0,
    dailySales: product.dailySalesRate || 0,
    currentCoverage: product.currentDaysCoverage || 0,
    recommendedQuantity: product.recommendedUnitsToProduceHumanReadable || 0,
    futureCoverage: product.futureDaysCoverage || 0,
    enabled: product.enabled ?? true, // Add enabled field from API
    isNd: product.isNd ?? false
  }));

  // Show loading state or actual data
  const displayProductSizes = batchPlanMutation.isPending && selectedSemiproduct 
    ? [] // Empty while loading
    : productGridData;


  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === TimePeriod.CustomPeriod) {
      // Seed Custom pickers from Q9M primary range so user has a sensible starting point.
      const range = calculateTimePeriodRange(TimePeriod.Q9M);
      if (range) {
        setFromDate(range.fromDate);
        setToDate(range.toDate);
      }
    }
    setSelectedTimePeriod(period);
    setNeedsRecalculation(true);
  };

  const getTimePeriodTooltip = (period: TimePeriod): string => {
    if (period === TimePeriod.CustomPeriod) {
      return `${fromDate.toLocaleDateString("cs-CZ")} - ${toDate.toLocaleDateString("cs-CZ")}`;
    }
    const range = calculateTimePeriodRange(period);
    if (!range) return getTimePeriodDisplayText(period);
    if (range.ranges && range.ranges.length > 1) {
      return range.ranges
        .map((r, i) => `Období ${String.fromCharCode('A'.charCodeAt(0) + i)}: ${r.from.toLocaleDateString("cs-CZ")} – ${r.to.toLocaleDateString("cs-CZ")}`)
        .join("\n");
    }
    return `${range.fromDate.toLocaleDateString("cs-CZ")} - ${range.toDate.toLocaleDateString("cs-CZ")}`;
  };

  // Builds the period portion of any CalculateBatchPlanRequest.
  // Named periods send only timePeriod (backend resolves ranges).
  // CustomPeriod sends fromDate/toDate and no timePeriod.
  const buildPeriodPayload = () =>
    selectedTimePeriod === TimePeriod.CustomPeriod
      ? { fromDate, toDate, timePeriod: undefined }
      : { fromDate: undefined, toDate: undefined, timePeriod: selectedTimePeriod };

  const calculateBatchPlan = (semiproductCode: string) => {
    const requestData: any = {
      productCode: semiproductCode,
      controlMode: controlMode,
      ...buildPeriodPayload(),
      salesMultiplier: salesMultiplier,
          ndCoverageFactor: ndCoverageFactor,
      directSemiproductAmount: directSemiproductAmount > 0 ? directSemiproductAmount : undefined,
    };

    // Add mode-specific parameters
    switch (controlMode) {
      case BatchPlanControlMode.MmqMultiplier:
        requestData.mmqMultiplier = mmqMultiplier;
        break;
      case BatchPlanControlMode.TotalWeight:
        requestData.totalWeightToUse = totalBatchSize;
        break;
      case BatchPlanControlMode.TargetDaysCoverage:
        requestData.targetDaysCoverage = targetDaysCoverage;
        break;
    }

    const request = new CalculateBatchPlanRequest(requestData);
    batchPlanMutation.mutate(request);
    setNeedsRecalculation(false); // Clear the flag after calculation
  };

  const calculateBatchPlanWithConstraints = (semiproductCode: string) => {
    // Convert constraints Map to ProductSizeConstraint array
    const constraints = Array.from(productConstraints.entries()).map(([productCode, constraint]) =>
      new ProductSizeConstraint({
        productCode,
        isFixed: constraint.isFixed,
        fixedQuantity: constraint.quantity
      })
    );

    const requestData: any = {
      productCode: semiproductCode,
      controlMode: controlMode,
      ...buildPeriodPayload(),
      salesMultiplier: salesMultiplier,
          ndCoverageFactor: ndCoverageFactor,
      productConstraints: constraints,
      directSemiproductAmount: directSemiproductAmount > 0 ? directSemiproductAmount : undefined,
    };

    // Add mode-specific parameters
    switch (controlMode) {
      case BatchPlanControlMode.MmqMultiplier:
        requestData.mmqMultiplier = mmqMultiplier;
        break;
      case BatchPlanControlMode.TotalWeight:
        requestData.totalWeightToUse = totalBatchSize;
        break;
      case BatchPlanControlMode.TargetDaysCoverage:
        requestData.targetDaysCoverage = targetDaysCoverage;
        break;
    }

    const request = new CalculateBatchPlanRequest(requestData);
    batchPlanMutation.mutate(request);
    setNeedsRecalculation(false); // Clear the flag after calculation
  };

  const handleSemiproductSelect = (product: CatalogItemDto | null) => {
    setSelectedSemiproduct(product);
    // Reset form when changing product
    setMmqMultiplier(1.0);
    setTotalBatchSize(0);
    setTargetDaysCoverage(30);
    setDirectSemiproductAmount(0);
    setInputMode('multiplier');
    setControlMode(BatchPlanControlMode.MmqMultiplier); // Reset to default mode
    // Clear constraints when changing product
    setProductConstraints(new Map());
    setNeedsRecalculation(false); // Clear recalculation flag when selecting new product
    
    // Auto-trigger calculation immediately after product selection
    if (product?.productCode) {
      const requestData: any = {
        productCode: product.productCode,
        controlMode: BatchPlanControlMode.MmqMultiplier,
        ...buildPeriodPayload(),
        salesMultiplier: salesMultiplier,
          ndCoverageFactor: ndCoverageFactor,
        mmqMultiplier: 1.0, // Use reset value
      };

      const request = new CalculateBatchPlanRequest(requestData);
      batchPlanMutation.mutate(request);
    }
  };


  const handleProductFixedChange = (productCode: string, isFixed: boolean, currentRecommendedQuantity: number) => {
    setProductConstraints(prev => {
      const newConstraints = new Map(prev);
      if (isFixed) {
        newConstraints.set(productCode, { isFixed: true, quantity: currentRecommendedQuantity });
      } else {
        newConstraints.delete(productCode);
      }
      return newConstraints;
    });
    setNeedsRecalculation(true);
  };

  const handleProductQuantityChange = (productCode: string, quantity: number) => {
    setProductConstraints(prev => {
      const newConstraints = new Map(prev);
      const existing = newConstraints.get(productCode);
      if (existing?.isFixed) {
        newConstraints.set(productCode, { isFixed: true, quantity });
      }
      return newConstraints;
    });
    setNeedsRecalculation(true);
  };

  // Manual calculation trigger
  const handleManualCalculate = () => {
    if (selectedSemiproduct?.productCode) {
      if (productConstraints.size > 0) {
        calculateBatchPlanWithConstraints(selectedSemiproduct.productCode);
      } else {
        calculateBatchPlan(selectedSemiproduct.productCode);
      }
    }
  };

  // Handle create manufacture order from batch planning
  const handleCreateOrder = async () => {
    if (!response?.success || !response.semiproduct || !response.summary || !selectedSemiproduct) {
      return;
    }

    // Get all products that can be manufactured from this semiproduct (including zero quantities)
    const productsToManufacture = response.productSizes
      ?.map(product => ({
        productCode: product.productCode || "",
        productName: product.productName || "",
        plannedQuantity: product.recommendedUnitsToProduceHumanReadable || 0
      })) || [];

    if (productsToManufacture.length === 0) {
      alert("Žádné produkty nenalezeny pro tento polotovar");
      return;
    }

    // Use prefilled manufacturing date if available, otherwise default to today
    let plannedDate: Date;
    
    if (prefilledManufacturingDate) {
      // Use the date selected in the calendar
      plannedDate = new Date(prefilledManufacturingDate);
      console.log('Using prefilled date:', { plannedDate });
    } else {
      // Default to today
      plannedDate = new Date();
      console.log('Using default date:', { plannedDate });
    }

    try {
      // Use exact user input values instead of calculated results
      let userRequestedBatchSize: number;
      
      switch (controlMode) {
        case BatchPlanControlMode.TotalWeight:
          userRequestedBatchSize = totalBatchSize;
          break;
        case BatchPlanControlMode.MmqMultiplier:
          userRequestedBatchSize = productMMQ ? (productMMQ * mmqMultiplier) : (response.summary.actualTotalWeight || 0);
          break;
        default:
          userRequestedBatchSize = response.summary.actualTotalWeight || 0;
          break;
      }

      const orderRequest = new CreateManufactureOrderRequest({
        productCode: response.semiproduct.productCode || "",
        productName: response.semiproduct.productName || "",
        originalBatchSize: response.semiproduct.minimalManufactureQuantity || 0,
        newBatchSize: userRequestedBatchSize,
        scaleFactor: response.summary.effectiveMmqMultiplier || 1.0,
        products: productsToManufacture.map(p => new CreateManufactureOrderProductRequest({
          productCode: p.productCode,
          productName: p.productName,
          plannedQuantity: p.plannedQuantity
        })),
        plannedDate: plannedDate,
        responsiblePerson: undefined,
        manufactureType: response.manufactureType,
        directSemiproductAmount: response.manufactureType !== ManufactureType.SinglePhase ? directSemiproductAmount : undefined,
      });

      const orderResponse = await createOrderMutation.mutateAsync(orderRequest);
      
      if (orderResponse.success && orderResponse.id) {
        // Store the order date for navigation
        setCreatedOrderDate(plannedDate);
        
        // Invalidate calendar queries to refresh calendar data
        queryClient.invalidateQueries({ 
          queryKey: [...QUERY_KEYS.manufactureOrders, "calendar"],
          refetchType: 'active' // Only refetch queries that are currently active
        });
        
        console.log('Calendar queries invalidated for order created on:', plannedDate);
        
        // Open the manufacture order detail modal
        setCreatedOrderId(orderResponse.id);
        setShowOrderModal(true);
      }
    } catch (error) {
      console.error("Error creating manufacture order:", error);
      alert("Chyba při vytváření zakázky. Zkuste to prosím znovu.");
    }
  };

  return (
    <div className={`flex flex-col ${PAGE_CONTAINER_HEIGHT} bg-gray-50 dark:bg-graphite-surface-2`}>
      {/* Header */}
      <div className="bg-white dark:bg-graphite-surface border-b dark:border-graphite-border px-6 py-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <Calculator className="w-6 h-6 text-indigo-600 dark:text-graphite-accent" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-graphite-text">
            Plánovač výrobních dávek
          </h1>
        </div>
        <p className="text-sm text-gray-600 dark:text-graphite-muted mt-1">
          Optimalizace distribuce polotovaru napříč různými velikostmi produktů
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          <div className="space-y-6">
            {/* Semiproduct Selection and Batch Settings */}
            <div className="bg-white dark:bg-graphite-surface rounded-lg shadow-sm dark:shadow-soft-dark border dark:border-graphite-border p-6 relative z-50">
              <h2 className="text-lg font-medium text-gray-900 dark:text-graphite-text mb-4 flex items-center">
                <Package className="w-5 h-5 text-gray-500 dark:text-graphite-muted mr-2" />
                Výběr polotovaru a nastavení
              </h2>
              
              <div className="space-y-4">
                {/* Top Row: Product Selection and Sales Settings */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left: Product Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-graphite-muted mb-2">
                      Polotovar / Produkt <span className="text-red-500 dark:text-red-400">*</span>
                    </label>
                    <div className="relative z-50">
                      <CatalogAutocomplete
                        value={selectedSemiproduct}
                        onSelect={handleSemiproductSelect}
                        placeholder="Vyberte polotovar nebo produkt..."
                        productTypes={[ProductType.SemiProduct, ProductType.Product]}
                        withBomOnly={true}
                        size="md"
                        clearable={true}
                      />
                    </div>
                  </div>

                  {/* Right: Sales Settings */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-600 dark:text-graphite-muted flex items-center">
                      <Settings className="w-3 h-3 text-gray-500 dark:text-graphite-muted mr-1" />
                      Nastavení prodejů
                    </h4>

                    {/* Multiplier + Period in one row */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-600 dark:text-graphite-muted whitespace-nowrap">Multiplikátor:</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          max="9.9"
                          value={salesMultiplier.toFixed(1)}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            if (!Number.isFinite(next) || next < 0.1 || next > 9.9) return;
                            setSalesMultiplier(next);
                            setNeedsRecalculation(true);
                          }}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-graphite-border dark:bg-graphite-surface-2 dark:text-graphite-text rounded text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          title="Sales Multiplier (1.0-9.9)"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label htmlFor="nd-coverage-factor" className="text-xs text-gray-600 dark:text-graphite-muted whitespace-nowrap">Faktor ND:</label>
                        <input
                          id="nd-coverage-factor"
                          type="number"
                          step="0.1"
                          min="0.1"
                          max="5"
                          value={ndCoverageFactor}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            if (!Number.isFinite(next) || next < 0.1 || next > 5) return;
                            setNdCoverageFactor(next);
                            setNeedsRecalculation(true);
                          }}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-graphite-border dark:bg-graphite-surface-2 dark:text-graphite-text rounded text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          title="ND factor: 1.2 = ND (star) sizes end with 20 % more days of stock than the others"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                      <label
                        htmlFor="sales-period-select"
                        className="text-xs text-gray-600 dark:text-graphite-muted whitespace-nowrap"
                      >
                        Časové období:
                      </label>
                      <select
                        id="sales-period-select"
                        value={selectedTimePeriod}
                        onChange={(e) => handleTimePeriodChange(e.target.value as TimePeriod)}
                        className="px-2 py-1 text-sm border border-gray-300 dark:border-graphite-border rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-graphite-surface-2 dark:text-graphite-text"
                        title={getTimePeriodTooltip(selectedTimePeriod)}
                      >
                        {[
                          TimePeriod.PreviousQuarter,
                          TimePeriod.FutureQuarter,
                          TimePeriod.Y2Y,
                          TimePeriod.PreviousSeason,
                          TimePeriod.Q9M,
                          TimePeriod.CustomPeriod,
                        ].map((period) => (
                          <option key={period} value={period}>
                            {getTimePeriodDisplayText(period)}
                          </option>
                        ))}
                      </select>
                      </div>
                    </div>

                    {/* Custom date pickers — only visible when CustomPeriod is selected */}
                    {selectedTimePeriod === TimePeriod.CustomPeriod && (
                      <div className="flex gap-3 items-center">
                        <label htmlFor="sales-from-date" className="text-xs text-gray-600 dark:text-graphite-muted whitespace-nowrap">Datum od:</label>
                        <input
                          id="sales-from-date"
                          type="date"
                          value={fromDate.toLocaleDateString('sv')}
                          onChange={(e) => {
                            setFromDate(new Date(e.target.value));
                            setNeedsRecalculation(true);
                          }}
                          className="w-44 px-2 py-1 text-sm border border-gray-300 dark:border-graphite-border dark:bg-graphite-surface-2 dark:text-graphite-text rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <label htmlFor="sales-to-date" className="text-xs text-gray-600 dark:text-graphite-muted whitespace-nowrap">do:</label>
                        <input
                          id="sales-to-date"
                          type="date"
                          value={toDate.toLocaleDateString('sv')}
                          onChange={(e) => {
                            setToDate(new Date(e.target.value));
                            setNeedsRecalculation(true);
                          }}
                          className="w-44 px-2 py-1 text-sm border border-gray-300 dark:border-graphite-border dark:bg-graphite-surface-2 dark:text-graphite-text rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Row: Batch Calculation Settings (only when product is selected) */}
                {selectedSemiproduct && (
                  <div className="space-y-3">
                    {/* Control Mode Selection */}
                    <div className="flex items-center gap-4">
                      <label className="text-sm font-medium text-gray-700 dark:text-graphite-muted">Režim řízení:</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="controlMode"
                            checked={controlMode === BatchPlanControlMode.MmqMultiplier}
                            onChange={() => {
                              setControlMode(BatchPlanControlMode.MmqMultiplier);
                              setNeedsRecalculation(true);
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-graphite-border"
                          />
                          <span className="text-sm text-gray-700 dark:text-graphite-muted">MMQ Multiplikátor</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="controlMode"
                            checked={controlMode === BatchPlanControlMode.TotalWeight}
                            onChange={() => {
                              setControlMode(BatchPlanControlMode.TotalWeight);
                              setNeedsRecalculation(true);
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-graphite-border"
                          />
                          <span className="text-sm text-gray-700 dark:text-graphite-muted">Celková hmotnost</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="controlMode"
                            checked={controlMode === BatchPlanControlMode.TargetDaysCoverage}
                            onChange={() => {
                              setControlMode(BatchPlanControlMode.TargetDaysCoverage);
                              setNeedsRecalculation(true);
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-graphite-border"
                          />
                          <span className="text-sm text-gray-700 dark:text-graphite-muted">Cílová zásoba (dny)</span>
                        </label>
                      </div>
                    </div>

                    {/* Mode-specific inputs */}
                    <div className="flex items-center gap-3">
                      {/* MMQ Multiplier Mode */}
                      {controlMode === BatchPlanControlMode.MmqMultiplier && (
                        <>
                          {/* MMQ */}
                          <div className="flex-none">
                            <input
                              type="number"
                              value={productMMQ || ""}
                              readOnly
                              placeholder={batchPlanMutation.isPending ? "MMQ..." : "MMQ"}
                              className="w-24 px-3 py-2 text-sm border border-gray-300 dark:border-graphite-border rounded-md bg-gray-50 dark:bg-graphite-surface-2 text-gray-600 dark:text-graphite-muted text-center"
                            />
                          </div>

                          {/* Multiplier with × symbol */}
                          <div className="flex items-center gap-2 flex-none">
                            <span className="text-gray-400 dark:text-graphite-faint text-lg">×</span>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={mmqMultiplier.toFixed(1)}
                              onChange={(e) => {
                                setMmqMultiplier(Number(e.target.value));
                                setNeedsRecalculation(true);
                              }}
                              placeholder="Multiplikátor"
                              className="w-24 px-3 py-2 text-sm border border-indigo-300 dark:border-graphite-border bg-white dark:bg-graphite-surface-2 text-gray-900 dark:text-graphite-text rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center"
                            />
                          </div>
                          
                          {/* Result */}
                          <div className="flex items-center gap-2 flex-none">
                            <span className="text-gray-400 dark:text-graphite-faint text-lg">=</span>
                            <input
                              type="number"
                              value={productMMQ ? (productMMQ * mmqMultiplier).toFixed(0) : ""}
                              readOnly
                              placeholder="Výsledek"
                              className="w-28 px-3 py-2 text-sm border border-indigo-300 dark:border-graphite-accent/40 bg-indigo-50 dark:bg-graphite-accent/10 text-indigo-800 dark:text-graphite-accent rounded-md text-center font-medium"
                            />
                          </div>
                        </>
                      )}

                      {/* Total Weight Mode */}
                      {controlMode === BatchPlanControlMode.TotalWeight && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-700 dark:text-graphite-muted">Celková hmotnost:</label>
                          <input
                            type="number"
                            min="0"
                            value={totalBatchSize}
                            onChange={(e) => {
                              setTotalBatchSize(Number(e.target.value));
                              setNeedsRecalculation(true);
                            }}
                            placeholder="Celková hmotnost"
                            className="w-32 px-3 py-2 text-sm border border-indigo-300 dark:border-graphite-border bg-white dark:bg-graphite-surface-2 text-gray-900 dark:text-graphite-text rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center"
                          />
                          <span className="text-sm text-gray-600 dark:text-graphite-muted">ml/g</span>
                        </div>
                      )}

                      {/* Target Days Coverage Mode */}
                      {controlMode === BatchPlanControlMode.TargetDaysCoverage && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-700 dark:text-graphite-muted">Cílová zásoba:</label>
                          <input
                            type="number"
                            min="1"
                            value={targetDaysCoverage}
                            onChange={(e) => {
                              setTargetDaysCoverage(Number(e.target.value));
                              setNeedsRecalculation(true);
                            }}
                            placeholder="Počet dní"
                            className="w-20 px-3 py-2 text-sm border border-indigo-300 dark:border-graphite-border bg-white dark:bg-graphite-surface-2 text-gray-900 dark:text-graphite-text rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center"
                          />
                          <span className="text-sm text-gray-600 dark:text-graphite-muted">dní</span>
                        </div>
                      )}

                      {/* Calculate Button */}
                      <div className="flex-none ml-2">
                        <button
                          onClick={handleManualCalculate}
                          disabled={!selectedSemiproduct || batchPlanMutation.isPending}
                          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {batchPlanMutation.isPending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Calculator className="w-4 h-4" />
                          )}
                          Přepočítat
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            

            {/* Product Grid - Only show if semiproduct is selected */}
            {selectedSemiproduct && (
                <div className="bg-white dark:bg-graphite-surface rounded-lg shadow-sm dark:shadow-soft-dark border dark:border-graphite-border">
                  <div className="px-6 py-4 border-b dark:border-graphite-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-graphite-text flex items-center">
                          <Package className="w-5 h-5 text-green-500 dark:text-emerald-400 mr-2" />
                          {response?.manufactureType === ManufactureType.SinglePhase ? 'Produkt' : 'Velikosti produktů'}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-graphite-muted mt-1">
                          {response?.manufactureType === ManufactureType.SinglePhase 
                            ? `Jednofázová výroba produktu ${selectedSemiproduct.productName}`
                            : `Produkty vyráběné z polotovaru ${selectedSemiproduct.productName}`
                          }
                        </p>
                      </div>
                      
                      {/* Create Order Button */}
                      {response?.success && (
                        response?.manufactureType === ManufactureType.SinglePhase || 
                        response.productSizes?.some(p => (p.recommendedUnitsToProduceHumanReadable || 0) > 0)
                      ) && (
                        <button
                          onClick={handleCreateOrder}
                          disabled={createOrderMutation.isPending || needsRecalculation}
                          className="bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          title={needsRecalculation ? "Před vytvořením objednávky je nutný přepočet dávek" : ""}
                        >
                          {createOrderMutation.isPending ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Vytváří se...
                            </>
                          ) : needsRecalculation ? (
                            <>
                              <FileText className="h-4 w-4" />
                              Vytvořit zakázku (nutný přepočet)
                            </>
                          ) : (
                            <>
                              <FileText className="h-4 w-4" />
                              Vytvořit zakázku
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-graphite-border">
                      <thead className="bg-gray-50 dark:bg-graphite-surface-2">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Kód produktu</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Název produktu</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Hmotnost</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Sklad eshop celkem</th>
                          <th
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase"
                            title="Množství z již naplánovaných výrobních zakázek. Zakázky ve stavu Koncept se nezapočítávají. Údaj se aktualizuje po každé změně zakázky."
                          >
                            Plánováno
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Denní prodeje</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Zásoba (dny)</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Množství / Fixed</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase">Budoucí zásoba (dny)</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-graphite-surface divide-y divide-gray-200 dark:divide-graphite-border">
                        {batchPlanMutation.isPending && selectedSemiproduct ? (
                          <tr>
                            <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-graphite-muted">
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                <span className="ml-2">Načítání dat...</span>
                              </div>
                            </td>
                          </tr>
                        ) : displayProductSizes.length === 0 && selectedSemiproduct ? (
                          <tr>
                            <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-graphite-muted">
                              Žádné produkty nenalezeny
                            </td>
                          </tr>
                        ) : (
                          displayProductSizes.map((product) => (
                            <tr key={product.productCode} className={`hover:bg-gray-50 dark:hover:bg-white/5 ${!product.enabled ? 'bg-gray-100 dark:bg-graphite-surface-2 text-gray-500 dark:text-graphite-muted' : ''}`}>
                              <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-graphite-text">
                                {product.productCode}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-graphite-text">
                                {product.productName}
                                <NdStar isNd={product.isNd} className="ml-1.5" />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-graphite-text">
                                {product.netWeight.toFixed(0)} g
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-graphite-text">
                                {product.stockEshopTotal.toFixed(0)} ks
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-graphite-text">
                                {product.plannedQuantity > 0 ? (
                                  <span className="font-semibold">{product.plannedQuantity.toFixed(0)} ks</span>
                                ) : (
                                  <span className="text-gray-400 dark:text-graphite-faint">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-graphite-text">
                                {product.dailySales.toFixed(2)} ks
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm px-2 py-1 rounded-full ${
                                  product.currentCoverage > 2000
                                    ? 'bg-gray-100 dark:bg-graphite-surface-2 text-gray-600 dark:text-graphite-muted'
                                    : product.currentCoverage < 7
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                      : product.currentCoverage < 14
                                        ? 'bg-yellow-100 dark:bg-amber-900/30 text-yellow-800 dark:text-amber-300'
                                        : 'bg-green-100 dark:bg-emerald-900/30 text-green-800 dark:text-emerald-300'
                                }`}>
                                  {product.currentCoverage > 2000 ? 'NA' : product.currentCoverage.toFixed(1)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0"
                                    value={productConstraints.get(product.productCode || '')?.quantity ?? product.recommendedQuantity ?? 0}
                                    onChange={(e) => handleProductQuantityChange(product.productCode || '', parseInt(e.target.value) || 0)}
                                    readOnly={!(productConstraints.get(product.productCode || '')?.isFixed ?? false)}
                                    className={`w-16 px-2 py-1 text-sm border rounded focus:ring-indigo-500 focus:border-indigo-500 ${
                                      !(productConstraints.get(product.productCode || '')?.isFixed ?? false)
                                        ? 'border-gray-300 dark:border-graphite-border bg-gray-50 dark:bg-graphite-surface-2 text-gray-600 dark:text-graphite-muted cursor-not-allowed'
                                        : response?.success === false && (productConstraints.get(product.productCode || '')?.isFixed ?? false)
                                        ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 text-red-900 dark:text-red-300' // Red styling for fixed products when there's an error
                                        : 'border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface-2 text-gray-900 dark:text-graphite-text'
                                    }`}
                                  />
                                  <span className="text-sm text-gray-500 dark:text-graphite-muted">ks</span>
                                  <input
                                    type="checkbox"
                                    checked={productConstraints.get(product.productCode || '')?.isFixed ?? false}
                                    onChange={(e) => handleProductFixedChange(product.productCode || '', e.target.checked, product.recommendedQuantity ?? 0)}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-graphite-border rounded"
                                    title="Fixovat množství"
                                  />
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm px-2 py-1 rounded-full ${
                                  product.futureCoverage > 2000
                                    ? 'bg-gray-100 dark:bg-graphite-surface-2 text-gray-600 dark:text-graphite-muted'
                                    : product.futureCoverage < 7
                                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                      : product.futureCoverage < 14
                                        ? 'bg-yellow-100 dark:bg-amber-900/30 text-yellow-800 dark:text-amber-300'
                                        : 'bg-green-100 dark:bg-emerald-900/30 text-green-800 dark:text-emerald-300'
                                }`}>
                                  {product.futureCoverage > 2000 ? 'NA' : product.futureCoverage.toFixed(1)}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                        {/* Direct Semiproduct Amount row (MultiPhase only) */}
                        {response?.manufactureType !== ManufactureType.SinglePhase && response?.manufactureType !== undefined && (
                          <tr className="bg-amber-50 dark:bg-amber-900/20 border-t-2 border-amber-200 dark:border-amber-700">
                            <td className="px-4 py-3 text-sm font-mono text-amber-800 dark:text-amber-300">
                              {response.semiproduct?.productCode}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-amber-800 dark:text-amber-300">
                              Přímý výstup
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={directSemiproductAmount}
                                  onChange={(e) => {
                                    setDirectSemiproductAmount(Number(e.target.value));
                                    setNeedsRecalculation(true);
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-amber-300 dark:border-amber-700 bg-white dark:bg-graphite-surface-2 text-amber-900 dark:text-amber-300 rounded focus:ring-amber-500 focus:border-amber-500 text-center"
                                />
                                <span className="text-sm text-amber-700 dark:text-amber-400">g</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400 dark:text-graphite-faint">—</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>

      {/* Manufacture Order Detail Modal */}
      {showOrderModal && createdOrderId && (
        <ManufactureOrderDetail
          orderId={createdOrderId}
          isOpen={showOrderModal}
          onClose={() => {
            setShowOrderModal(false);
            setCreatedOrderId(null);
            // Navigate to weekly calendar showing the week of the created order
            const orderDate = createdOrderDate || new Date();
            const dateString = orderDate.toISOString().split('T')[0];
            navigate(`/manufacturing/orders?view=weekly&date=${dateString}`);
            setCreatedOrderDate(null);
          }}
        />
      )}
    </div>
  );
};

export default BatchPlanningCalculator;